import React, { Fragment, type ReactNode } from "react"

import { useTranslation } from "react-i18next"
import { useIsFetching } from "react-query"
import { useSelector } from "react-redux"

import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined"
import { Grid } from "@mui/material"
import { Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import type { MRT_ColumnDef } from "material-react-table"

import { useWorkflowFeatures } from "../../features/Workflows/utils/workflowHelpers"
import type { RootState } from "../../store/ReduxStore"
import { Deployment } from "../../types/generated/api/Deployment"
import type { ModelDeploymentSchemaFeature } from "../../types/generated/api/ModelDeploymentSchemaFeature"
import { BaseTable } from "./BaseTable"

import styles from "../dialogs/WorkflowConfigurationDialog.module.scss"

export function ProjectSchemaTable(): React.ReactElement {
  const { t } = useTranslation("tables")
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const schemaFeatures = useWorkflowFeatures(new Set(["SchemaFeatures"]))

  const isCurrentProjectFetching = useIsFetching(["project"]) > 0

  // columns for project schema table
  const columns: MRT_ColumnDef<ModelDeploymentSchemaFeature>[] = [
    {
      header: t("projectSchema.columns.featureName"),
      accessorKey: "name",
      Cell: ({ row }) => {
        if (schemaFeatures.length > 0) {
          return <Typography variant="p">{schemaFeatures[row.index]?.name}</Typography>
        }
      },
    },
    {
      header: t("projectSchema.columns.type"),
      accessorKey: "type",
      Cell: ({ row }) => {
        if (schemaFeatures.length > 0) {
          return (
            <Typography style={{ textTransform: "capitalize" }} variant="p">
              {schemaFeatures[row.index]?.type?.toLowerCase()}
            </Typography>
          )
        }
      },
    },
    {
      header: t("projectSchema.columns.required"),
      accessorKey: "required",
      Cell: ({ row }) => {
        if (schemaFeatures.length > 0) {
          return schemaFeatures[row.index]?.isRequired ? (
            <CheckOutlinedIcon className={styles.iconColor} fontSize="inherit" />
          ) : (
            <Typography variant="p">__</Typography>
          )
        }
      },
    },
    {
      header: t("projectSchema.columns.source"),
      accessorKey: "source",
      Cell: ({ row }) => {
        if (schemaFeatures.length > 0) {
          const sources = schemaFeatures[row.index]?.workflows ?? []

          // Extracting the first item
          const firstItem = sources.slice(0, 1)

          // Checking if there are more items
          const moreItemsCount = sources.length - 1
          const hasMoreItems = moreItemsCount > 0

          // Rendering logic
          let renderedItems: string | ReactNode = firstItem
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ?.map((it: any) => {
              return `${it?.name} ${currentProject?.category === Deployment.category.WORKFLOW ? `v${parseFloat(it?.version).toFixed(1)}` : ""}`
            })
            .join(", ")
          if (hasMoreItems) {
            renderedItems = (
              <Typography variant="p" noWrap>
                {`${renderedItems}`}{" "}
                <span style={{ textDecoration: "underline" }}>
                  {t("projectSchema.tooltip.andMore", { count: moreItemsCount })}
                </span>
              </Typography>
            )
          }

          return (
            <Tooltip
              placement="top"
              title={
                <Grid item padding={1}>
                  {t("projectSchema.tooltip.title")}
                  <Grid item container marginTop={0.5}>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {sources?.map((wf: any, idx: number) => {
                      return (
                        <Fragment key={`${row.original.name}-${wf?.name}-v${wf?.version}`}>
                          {idx !== sources?.length - 1 || sources?.length === 1 ? "" : "and "}
                          {wf?.name}{" "}
                          {currentProject?.category === Deployment.category.WORKFLOW
                            ? `v${parseFloat(wf?.version)?.toFixed(1)}`
                            : ""}
                          {idx !== sources?.length - 1 ? ", " : " "}
                        </Fragment>
                      )
                    })}
                  </Grid>
                </Grid>
              }
            >
              <Typography variant="p" noWrap>
                {sources.length > 0 ? renderedItems : "_"}
              </Typography>
            </Tooltip>
          )
        }
      },
    },
  ]

  return (
    <BaseTable
      data={schemaFeatures}
      columns={columns as MRT_ColumnDef[]}
      disableToolbar
      title="Project Schema"
      pageSize={10}
      isLoading={isCurrentProjectFetching}
      enableOrdering={false}
    />
  )
}
